import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '@/api/client';
import { useCreateUser, useUpdateUser } from '@/api/users';
import type { Role, Team, User, WorkspaceRole } from '@/api/types';
import { Button, Checkbox, FieldShell, Input, Modal, Select, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { humanize } from '@/lib/format';
import { ROLE_HELP, assignableRoles, canManageUser, parseSkills } from './helpers';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120),
  email: z.string().trim().email('Enter a valid email'),
  password: z.string().max(128),
  role: z.enum(['super_admin', 'owner', 'admin', 'supervisor', 'agent']),
  teamIds: z.array(z.string()),
  skills: z
    .string()
    .refine(
      (s) => parseSkills(s).every((x) => /^[a-z0-9_-]{1,40}$/.test(x)),
      'Skills are words like payments, instagram, hindi',
    ),
  maxConcurrentChats: z.coerce.number().int().min(0).max(50),
  maxOpenTickets: z.coerce.number().int().min(0).max(500),
  isActive: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export function UserFormModal({
  user,
  teams,
  onClose,
}: {
  user: User | null;
  teams: Team[];
  onClose: () => void;
}) {
  const { user: me, ability } = useAuth();
  const toast = useToast();
  const create = useCreateUser();
  const update = useUpdateUser();
  const isNew = !user;
  const isSelf = user?.id === me.id;
  const roles: Role[] = assignableRoles(ability);
  const canEditTarget = isNew || isSelf || canManageUser(ability, user.role);
  const roleOptions: Role[] = isSelf ? [me.role] : canEditTarget ? roles : [user.role];

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(
      // New users need a password; on edit, blank means "keep current".
      schema.refine((v) => (v.password.length === 0 ? !isNew : v.password.length >= 10), {
        message: 'At least 10 characters',
        path: ['password'],
      }),
    ),
    defaultValues: {
      name: user?.name ?? '',
      email: user?.email ?? '',
      password: '',
      role: user?.role ?? 'agent',
      teamIds: user?.teamIds ?? [],
      skills: user?.skills.join(', ') ?? '',
      maxConcurrentChats: user?.maxConcurrentChats ?? 5,
      maxOpenTickets: user?.maxOpenTickets ?? 25,
      isActive: user?.isActive ?? true,
    },
  });

  const onError = (err: Error) => {
    if (err instanceof ApiError) {
      const fields = err.fieldErrors;
      for (const [path, message] of Object.entries(fields)) setError(path as keyof FormValues, { message });
      if (err.status === 409) setError('email', { message: 'An account with this email already exists' });
      if (Object.keys(fields).length === 0 && err.status !== 409) toast.error(err.message);
    } else toast.error(err.message);
  };

  const onSubmit = handleSubmit((v) => {
    const common = {
      name: v.name,
      email: v.email,
      teamIds: v.teamIds,
      skills: parseSkills(v.skills),
      maxConcurrentChats: v.maxConcurrentChats,
      maxOpenTickets: v.maxOpenTickets,
    };
    if (isNew) {
      create.mutate(
        { ...common, password: v.password, role: v.role as WorkspaceRole },
        { onSuccess: (u) => (toast.success(`${u.name} can now sign in`), onClose()), onError },
      );
    } else {
      update.mutate(
        {
          id: user.id,
          input: {
            ...common,
            ...(v.password ? { password: v.password } : {}),
            ...(isSelf ? {} : { role: v.role as WorkspaceRole, isActive: v.isActive }),
          },
        },
        { onSuccess: () => (toast.success('Saved'), onClose()), onError },
      );
    }
  });

  const pending = create.isPending || update.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isNew ? 'Add agent' : `Edit ${user.name}`}
      description={isNew ? 'They’ll sign in with this email and password.' : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="user-form" loading={pending} disabled={!canEditTarget}>
            {isNew ? 'Create account' : 'Save changes'}
          </Button>
        </>
      }
    >
      {!canEditTarget && (
        <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Your role can’t edit a {humanize(user!.role)}. Ask your workspace owner.
        </p>
      )}
      <form id="user-form" onSubmit={onSubmit} noValidate>
        <fieldset disabled={!canEditTarget} className="grid gap-4 sm:grid-cols-2">
          <Input label="Full name" required error={errors.name?.message} {...register('name')} />
          <Input label="Email" type="email" required error={errors.email?.message} {...register('email')} />
          <Input
            label={isNew ? 'Temporary password' : 'Reset password'}
            type="password"
            autoComplete="new-password"
            required={isNew}
            placeholder={isNew ? '' : 'Leave blank to keep current'}
            hint={
              isNew
                ? 'Min 10 characters. Ask them to change it after first login.'
                : 'Signs them out everywhere.'
            }
            error={errors.password?.message}
            {...register('password')}
          />
          <Select
            label="Role"
            disabled={isSelf}
            hint={
              isSelf
                ? 'You can’t change your own role.'
                : (ROLE_HELP[watch('role') as WorkspaceRole] ?? undefined)
            }
            error={errors.role?.message}
            {...register('role')}
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {humanize(r)}
              </option>
            ))}
          </Select>
          <Input
            label="Max concurrent chats"
            type="number"
            min={0}
            max={50}
            error={errors.maxConcurrentChats?.message}
            {...register('maxConcurrentChats')}
          />
          <Input
            label="Max open tickets"
            type="number"
            min={0}
            max={500}
            error={errors.maxOpenTickets?.message}
            {...register('maxOpenTickets')}
          />
          <Input
            wrapperClassName="sm:col-span-2"
            label="Skills"
            placeholder="payments, disputes, instagram, hindi, punjabi"
            hint="Comma-separated. Skills-based routing matches these to ticket requirements."
            error={errors.skills?.message}
            {...register('skills')}
          />
          <FieldShell id="teams" label="Teams" className="sm:col-span-2">
            {teams.length === 0 ? (
              <p className="text-sm text-slate-500">No teams yet.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {teams.map((t) => (
                  <Checkbox
                    key={t.id}
                    value={t.id}
                    label={t.name}
                    description={humanize(t.assignmentStrategy)}
                    {...register('teamIds')}
                  />
                ))}
              </div>
            )}
          </FieldShell>
          {!isNew && !isSelf && (
            <Checkbox
              className="sm:col-span-2"
              label="Active"
              description="Deactivated users can’t sign in and receive no tickets."
              {...register('isActive')}
            />
          )}
        </fieldset>
      </form>
    </Modal>
  );
}
